import * as React from "react";
import { useTranslation } from "../../i18n";
import { classnames, useUid } from "../../utils";
import { Button } from "../dm/button/button";
import { Heading } from "../dm/text/heading";
import * as styles from "./dialog.module.scss";

// `aria-labelledby` is omitted, not just overridden: the dialog's accessible name is owned by
// `title`, so accepting one and silently discarding it would be a lie in the type.
interface DialogProps extends Omit<
  React.DialogHTMLAttributes<HTMLDialogElement>,
  "open" | "title" | "onClose" | "aria-labelledby"
> {
  /** Becomes the dialog's accessible name via aria-labelledby. */
  title: React.ReactNode;
  /** 1+ actions, typically <Button>s. Rendered with equal widths. */
  footer?: React.ReactNode | undefined;
  /** Controlled open state. Omit it to run uncontrolled off `defaultOpen`. */
  open?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  onClose?: (() => void) | undefined;
  /** @default true */
  closeOnEscape?: boolean | undefined;
  /** @default true */
  closeOnBackdropClick?: boolean | undefined;
  /** Overrides the default "Schließen" accessible name on the close (X) button. */
  closeLabel?: string | undefined;
}

export const Dialog = ({
  title,
  children,
  footer,
  open,
  defaultOpen = false,
  onClose,
  closeOnEscape = true,
  closeOnBackdropClick = true,
  closeLabel,
  className,
  onClick,
  onMouseDown,
  ...otherProps
}: DialogProps): JSX.Element => {
  const { t } = useTranslation();
  const dialogRef = React.useRef<HTMLDialogElement | null>(null);
  const pressStartedOnBackdropRef = React.useRef(false);
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const titleId = `dialog-title-${useUid()}`;

  // showModal()/close() are imperative APIs — the `open` attribute alone produces a non-modal
  // dialog (no top layer, no backdrop, no focus trap), so state is synced onto the node here
  // rather than rendered as `open={isOpen}`.
  //
  // Deliberately runs on every render rather than on [isOpen]: the DOM can close itself without
  // React's state changing (Escape, or Chromium's anti-trap force-close described below). If a
  // controlled consumer's `onClose` doesn't set `open` back to false, `isOpen` never changes, so
  // an [isOpen]-keyed effect would never re-fire and the dialog would be stuck closed forever with
  // no way to reopen it. Re-asserting each render self-heals that drift; both calls are guarded by
  // `el.open`, so this is a no-op whenever the DOM already matches.
  React.useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (isOpen && !el.open) {
      el.showModal();
    } else if (!isOpen && el.open) {
      el.close();
    }
  });

  // Covers every way the dialog can close natively (Escape, close(), a future form method="dialog")
  // so React state can never drift from the DOM's own open state.
  React.useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => {
      if (!isControlled) {
        setInternalOpen(false);
      }
      onClose?.();
    };
    el.addEventListener("close", handleClose);
    return () => el.removeEventListener("close", handleClose);
  }, [isControlled, onClose]);

  // NOTE: Chromium does not honour a prevented `cancel` event indefinitely — verified against a
  // plain, framework-free <dialog> (no React involved) that the 3rd Escape press force-closes the
  // dialog even though preventDefault() ran on every press. This is a deliberate browser anti-trap
  // safeguard, not a bug here: it exists so a page can never build a truly inescapable modal. There
  // is no way to opt out of it from userland, and it should not be "fixed" by e.g. re-opening the
  // dialog on its `close` event — that would fight the browser's own accessibility protection.
  React.useEffect(() => {
    const el = dialogRef.current;
    if (!el || closeOnEscape) return;
    const handleCancel = (event: Event) => event.preventDefault();
    el.addEventListener("cancel", handleCancel);
    return () => el.removeEventListener("cancel", handleCancel);
  }, [closeOnEscape]);

  const requestClose = () => {
    dialogRef.current?.close();
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDialogElement>) => {
    onMouseDown?.(event);
    pressStartedOnBackdropRef.current = event.target === dialogRef.current;
  };

  const handleClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    onClick?.(event);
    // A backdrop click lands on the <dialog> element itself (::backdrop isn't a real target), so
    // this only fires for clicks outside .container — as long as the <dialog> carries no padding.
    //
    // The press must have *started* on the backdrop too. Without that check, drag-selecting text
    // inside the dialog and releasing outside it fires `click` on the nearest common ancestor —
    // the <dialog> — and would close it mid-selection.
    const startedOnBackdrop = pressStartedOnBackdropRef.current;
    pressStartedOnBackdropRef.current = false;
    if (closeOnBackdropClick && startedOnBackdrop && event.target === dialogRef.current) {
      requestClose();
    }
  };

  return (
    <dialog
      {...otherProps}
      ref={dialogRef}
      aria-labelledby={titleId}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      className={classnames({
        [`${className}`]: !!className,
        [styles.base]: true,
      })}
    >
      <div className={styles.container}>
        <header className={styles.header}>
          <Heading kind="primary" as="h2" id={titleId}>
            {title}
          </Heading>
          <Button kind="invisible" icon="cross" aria-label={closeLabel || t("dialog.close")} onClick={requestClose} />
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </dialog>
  );
};
